import {
    Action,
    Address,
    AddressActions,
    BasisActions,
    BookingflowStateMachine,
    BookableAssembler,
    BookingService,
    CalendarActions,
    GeneralActions,
    Goal,
    Partner,
    RepositoryAPI,
    RepositoryFake,
    Service,
    State,
    StateName,
    BookableSchedules,
} from '@raskrask-dk/bookingflow-state-machine'
import { ActionData } from '@raskrask-dk/bookingflow-state-machine/lib/action/action.types'
import {
    ActionConstructor,
    ActionOfState,
} from '@raskrask-dk/bookingflow-state-machine/lib/bookingflowstatemachine/bookingflowstatemachine.types'
import { Observer } from '@raskrask-dk/bookingflow-state-machine/lib/bookingflowstatemachine/subject'
import { RRDate } from '@raskrask-dk/raskrask-utils'
import React, { useEffect, useState } from 'react'
import { AnyAction, Dispatch } from 'redux'
import { BfsmRepoAdaptors } from '../adaptors/bfsmRepoAdaptors'
import { BasisTypeBF, MapperBF } from '../models'
import {
    setErrorcase,
    setIsLoading,
    setPreferedParnterId,
} from '../redux/reducers/appSettings'

const ContextBFSM = React.createContext<
    | [
          BookingflowStateMachine,
          (flow?: 'Booking' | 'MoveBooking' | 'CancelBooking') => void
      ]
    | undefined
>(undefined)

/**
 * This ensures there's only 1 BFSM.
 * When situation changes (state or actions) all consumers will rerender
 * @param Children which can consume this provider
 * @returns Provider for BFSM
 */

const ProviderBFSM = ({ children }: { children: JSX.Element }): JSX.Element => {
    const { REACT_APP_API_URL, REACT_APP_BASEURL, REACT_APP_REPOSITORY_MODE } =
        process.env

    const repo =
        REACT_APP_REPOSITORY_MODE === 'mock'
            ? new RepositoryFake()
            : new RepositoryAPI(
                  REACT_APP_API_URL ?? 'http://localhost:8080',
                  REACT_APP_BASEURL as string
              )

    const assembler = new BookableAssembler(MapperBF)
    const bookingService = new BookingService(repo, assembler)

    const [BFSM, setBFSM] = useState<BookingflowStateMachine>(
        new BookingflowStateMachine(bookingService, 'Booking')
    )

    const setNewBFSM = (flow?: FlowType) => {
        setBFSM(new BookingflowStateMachine(bookingService, flow))
    }

    return (
        <ContextBFSM.Provider value={[BFSM, setNewBFSM]}>
            {children}
        </ContextBFSM.Provider>
    )
}

/**
 * @returns BookingflowStateMachine provided through shared context
 * @throws if called outside context of ProviderBFSM
 */
const useBFSM = (): [BookingflowStateMachine, (flow?: FlowType) => void] => {
    const BFSM = React.useContext(ContextBFSM)
    if (!BFSM) throw new Error('BFSM must be accessed through ProviderBFSM')
    return BFSM
}

const useBFSMState = (): State<any, StateName> => {
    const [BFSM] = useBFSM()
    const [state, setState] = useState(BFSM.currentState())

    const observer: Observer = {
        id: Math.floor(Math.random() * 10000).toString(), // avoids collision (multiple could listen to same key)
        notify: () => setState(BFSM.currentState()),
    }

    const subscribe = () => BFSM.attach(observer)
    const unsubscribe = () => BFSM.detach(observer.id)

    useEffect(() => {
        setState(BFSM.currentState())
    }, [BFSM])

    useEffect(() => {
        subscribe()

        return () => unsubscribe()
    })
    return state
}

const useBFSMCanDo = <
    S extends State<any, StateName>,
    GAction extends Action<string, any>
>(
    action: ActionConstructor<ActionOfState<S>, S, ActionData<GAction>>
): boolean => {
    const [bfsm] = useBFSM()
    const [state, setState] = useState(bfsm.canDo(action))

    const observer: Observer = {
        id: Math.floor(Math.random() * 10000).toString(), // avoids collision (multiple could listen to same key)
        notify: () => setState(bfsm.canDo(action)),
    }

    const subscribe = () => bfsm.attach(observer)
    const unsubscribe = () => bfsm.detach(observer.id)

    useEffect(() => {
        subscribe()

        return () => unsubscribe()
    })

    return state
}

const initiateRebooking = async (
    BFSM: BookingflowStateMachine,
    bookingId: string | undefined,
    dispatch: Dispatch<AnyAction>
): Promise<void> => {
    try {
        setIsLoading(true)
        if (bookingId === undefined || bookingId === '') {
            setIsLoading(false)
            return
        }
        const previousBooking = await BFSM.repo.getBookingData(bookingId)

        if (previousBooking.address === 'INVALID') return

        dispatch(setPreferedParnterId(previousBooking.profileID))

        // just test values
        const address = new Address(
            previousBooking.address.address,
            previousBooking.address.geoLat,
            previousBooking.address.geoLng,
            previousBooking.address.zipCode,
            '0'
        )
        const customerType = 'Private'
        const basisType = new BasisTypeBF({
            name: previousBooking.basisName,
            id: previousBooking.basisTypeID,
            disabled: false,
            icon: '',
        })
        const goal = new Goal(previousBooking.goalID, previousBooking.goalName)
        const serviceType = new Service(
            previousBooking.serviceTypeID,
            previousBooking.serviceTypeName,
            previousBooking.serviceDuration,
            false
        )
        const partner = new Partner(
            previousBooking.profileID,
            previousBooking.alias
        )

        const klippeCardCode =
            previousBooking.klippeCardCode !== ''
                ? previousBooking.klippeCardCode
                : undefined

        // pre set customer details
        BFSM.addSuggestions = {
            customerName: previousBooking.cFullName,
            customerEmail: previousBooking.cEmail,
            customerPhone: previousBooking.cPhone,
            ...(klippeCardCode && { klippeCardCode: klippeCardCode }),
        }

        // address
        BFSM.doAction(AddressActions.Next, address)
        // basis
        await BFSM.service.getOfferedBasisTypes(
            address,
            new RRDate().toString('yyyy-MM-dd HH:mm'),
            28,
            [previousBooking.profileID]
        )
        BFSM.doAction(BasisActions.SetCustomerType, customerType)
        BFSM.doAction(BasisActions.SetBasisType, basisType)
        BFSM.doAction(BasisActions.Next, undefined)
        // calendar
        const bookingflowDTO: BookableSchedules =
            BfsmRepoAdaptors.toBookableSchedulesDTO(
                address!,
                basisType as BasisTypeBF,
                customerType!,
                new RRDate(),
                new RRDate().addDays(27)
            )
        bookingflowDTO.filters = { profileIds: [previousBooking.profileID] }
        await BFSM.service.loadSchedules(bookingflowDTO)
        BFSM.doAction(CalendarActions.SetGoal, goal)
        BFSM.doAction(CalendarActions.SetServiceType, serviceType)
        BFSM.doAction(CalendarActions.SetPartner, partner)
        setIsLoading(false)
    } catch (err) {
        dispatch(setErrorcase('RebookBadData'))
        BFSM.doAction(GeneralActions.GoToErrorState, undefined)
    }
}

const initiateBookingWithKlippeKort = async (
    BFSM: BookingflowStateMachine,
    suggestions: any,
    dispatch: Dispatch<AnyAction>
): Promise<void> => {
    try {
        setIsLoading(true)

        dispatch(setPreferedParnterId(parseInt(suggestions.profileId)))

        // address
        const address = new Address(
            suggestions.address.address,
            suggestions.address.geoLat,
            suggestions.address.geoLng,
            suggestions.address.zipCode,
            '0'
        )
        BFSM.doAction(AddressActions.Next, address)

        // basis
        const offeredBasisTpes = await BFSM.service.getOfferedBasisTypes(
            address,
            new RRDate().toString('yyyy-MM-dd HH:mm'),
            28
        )
        const customerType = suggestions.customerType
        BFSM.doAction(BasisActions.SetCustomerType, customerType)
        const basisTypeName = offeredBasisTpes.find((b) => {
            return (
                b.id === parseInt(suggestions.basisTypeId) &&
                b.customerTypes.includes(suggestions.customerType)
            )
        })?.name
        if (!basisTypeName) return
        const basisType = new BasisTypeBF({
            name: basisTypeName,
            id: parseInt(suggestions.basisTypeId),
            disabled: false,
            icon: '',
        })
        BFSM.doAction(BasisActions.SetBasisType, basisType)
        BFSM.doAction(BasisActions.Next, undefined)

        // calendar
        const bookingflowDTO: BookableSchedules =
            BfsmRepoAdaptors.toBookableSchedulesDTO(
                address,
                basisType,
                customerType,
                new RRDate(),
                new RRDate().addDays(27)
            )
        const schedulesData = await BFSM.service.loadSchedules(bookingflowDTO)
        const goalName = schedulesData.goals.find((g) => {
            return g.id === parseInt(suggestions.goalId)
        })?.name
        if (!goalName) return
        const goal = new Goal(parseInt(suggestions.goalId), goalName)
        BFSM.doAction(CalendarActions.SetGoal, goal)
        const { name: serviceTypeName, duration: serviceDuration } =
            schedulesData.services.find((s) => {
                return s.id === parseInt(suggestions.serviceTypeId)
            }) || { name: undefined, duration: undefined }
        if (!serviceTypeName || !serviceDuration) return
        const serviceType = new Service(
            parseInt(suggestions.serviceTypeId),
            serviceTypeName,
            serviceDuration.minutes,
            false
        )
        BFSM.doAction(CalendarActions.SetServiceType, serviceType)
        const alias = schedulesData.partners.find(
            (p) => p.id === parseInt(suggestions.profileId)
        )?.alias
        if (!alias) return
        const partner = new Partner(parseInt(suggestions.profileId), alias)
        BFSM.doAction(CalendarActions.SetPartner, partner)

        const klippeCardCode: string | undefined =
            await BFSM.repo.getLastKlippeCardCodeByPurchaserEmail(
                suggestions.customerEmail
            )

        // pre set customer details
        BFSM.addSuggestions = {
            customerName: suggestions.customerName,
            customerEmail: suggestions.customerEmail,
            customerPhone: suggestions.customerPhone,
            ...(klippeCardCode && { klippeCardCode: klippeCardCode }),
        }

        setIsLoading(false)
    } catch (err) {
        dispatch(setErrorcase('RebookBadData'))
        BFSM.doAction(GeneralActions.GoToErrorState, undefined)
    }
}

export {
    ProviderBFSM,
    useBFSMState,
    useBFSM,
    useBFSMCanDo,
    initiateRebooking,
    initiateBookingWithKlippeKort,
}
